/* 
  ========================================================================
  Ejemplos de uso del TableSkeleton para tus tablas en estado de carga:
  ========================================================================

  Genera 5 columnas de texto generico y 5 filas por defecto:
      <TableSkeleton />

  Genera 5 columnas de texto generico y 5 filas por defecto:
      <TableSkeleton
        headers={['Usuario', 'Email', 'Rol', 'Estado', 'Acciones']}
        columns={5}
        rows={10}
      />

  USO CON TIPOS DE COLUMNAS ESPECIFICOS (Recomendado para mayor fidelidad visual):
      <TableSkeleton
        headers={['Información', 'Contacto', 'Estado', 'Acciones']}
        columns={['avatar-text', 'text-two-lines', 'badge', 'actions']}
        rows={5}
      />
*/
import React from 'react'
import { Skeleton } from './skeleton'

export type SkeletonColumnType = 'avatar-text' | 'text' | 'text-two-lines' | 'badge' | 'actions';

export interface TableSkeletonProps {
  headers?: string[]
  columns?: number | SkeletonColumnType[]
  rows?: number
}

export function TableSkeleton({ headers, columns = 5, rows = 5 }: TableSkeletonProps) {
  // Generar la configuración de columnas basada en si es un número o un array
  const columnsConfig: SkeletonColumnType[] = Array.isArray(columns)
    ? columns
    : Array.from({ length: columns as number }).map((_, i) => {
      if (i === 0) return 'avatar-text';
      if (i === (columns as number) - 1) return 'actions';
      return 'text';
    });

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm w-full">
      <div className="overflow-x-auto">
        <table className="w-full whitespace-nowrap">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {headers ? headers.map((header, i) => (
                <th
                  key={i}
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 ${i === headers.length - 1 ? 'text-right' : ''}`}
                >
                  {header}
                </th>
              )) : columnsConfig.map((_, i) => (
                <th key={i} className="px-6 py-4 text-left">
                  <Skeleton className="h-4 w-24" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {columnsConfig.map((colType, colIndex) => (
                  <td key={colIndex} className="px-6 py-4">
                    {colType === 'avatar-text' && (
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    )}
                    {colType === 'text-two-lines' && (
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    )}
                    {colType === 'text' && (
                      <Skeleton className="h-4 w-28" />
                    )}
                    {colType === 'badge' && (
                      <Skeleton className="h-6 w-16 rounded-full" />
                    )}
                    {colType === 'actions' && (
                      <div className="flex items-center justify-end gap-2">
                        <Skeleton className="h-8 w-8 rounded" />
                        <Skeleton className="h-8 w-8 rounded" />
                        <Skeleton className="h-8 w-8 rounded" />
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
